import {
  useMemo,
  useState
} from 'react';

import {
  Bot,
  CheckCircle2,
  Eye,
  Loader2,
  Send,
  X,
  XCircle
} from 'lucide-react';

import {
  useRealtime
} from '../../contexts/RealtimeContext';


export function JarvisExecutionPanel() {

  const {
    activeJob,
    activeJobLogs,
    sendJarvisMessage
  } =
    useRealtime();


  const [showOutput, setShowOutput] =
    useState(
      false
    );


  const [sharing, setSharing] =
    useState(
      false
    );


  const [shareError, setShareError] =
    useState<string | null>(
      null
    );


  const output =
    useMemo(
      () =>
        activeJobLogs
          .map(
            log =>
              log.data
          )
          .join(
            ''
          ),
      [
        activeJobLogs
      ]
    );


  if (
    !activeJob
  ) {
    return null;
  }


  const terminal =
    [
      'succeeded',
      'failed',
      'cancelled',
      'timed_out'
    ].includes(
      activeJob.status
    );


  const running =
    activeJob.status ===
      'accepted' ||
    activeJob.status ===
      'running';


  if (
    !terminal &&
    !running
  ) {
    return null;
  }


  const shareResult =
    async () => {

      if (
        !output.trim() ||
        sharing
      ) {
        return;
      }


      setSharing(
        true
      );

      setShareError(
        null
      );


      const payload =
        output.length >
          80000
          ? output.slice(
              -80000
            )
          : output;


      const message =
        [
          'KAIZEN execution result shared from JARVIS.',
          `Job: ${activeJob.id}`,
          `Type: ${activeJob.type}`,
          `Status: ${activeJob.status}`,
          `Working directory: ${activeJob.workingDirectory}`,
          '',
          '--- BEGIN EXECUTION OUTPUT ---',
          payload,
          '--- END EXECUTION OUTPUT ---',
          '',
          'Continue from this execution result.'
        ].join(
          '\n'
        );


      try {

        await sendJarvisMessage(
          message,
          {
            projectId:
              activeJob.projectId,
            workingDirectory:
              activeJob.workingDirectory
          }
        );

      }
      catch (
        error:
          unknown
      ) {

        setShareError(
          error instanceof Error
            ? error.message
            : String(
                error
              )
        );

      }
      finally {

        setSharing(
          false
        );
      }
    };


  return (
    <div className="relative shrink-0 px-4 sm:px-5 pb-3">
      <div className="overflow-hidden rounded-xl border border-white/[0.07] bg-white/[0.025]">
        <div className="flex items-center justify-between gap-3 px-3.5 py-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-lg border border-indigo-400/15 bg-indigo-400/[0.07] text-indigo-300 flex items-center justify-center shrink-0">
              {running
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : activeJob.status === 'succeeded'
                  ? <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  : <XCircle className="w-4 h-4 text-rose-400" />}
            </div>

            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.1em] text-slate-500 font-semibold">
                KAIZEN Execution
              </div>
              <div className="text-xs text-slate-200 truncate mt-0.5">
                {activeJob.type} · {activeJob.status}
              </div>
            </div>
          </div>

          {terminal && (
            <div className="flex items-center gap-1.5 shrink-0">
              <button
                type="button"
                onClick={() => setShowOutput(true)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-white/[0.08] bg-white/[0.035] text-[10px] text-slate-300 hover:bg-white/[0.06]"
              >
                <Eye className="w-3.5 h-3.5" />
                View Output
              </button>

              <button
                type="button"
                disabled={!output.trim() || sharing}
                onClick={() => void shareResult()}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-indigo-400/20 bg-indigo-500/85 text-[10px] text-white hover:bg-indigo-500 disabled:opacity-40"
              >
                {sharing
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <Send className="w-3.5 h-3.5" />}
                Share with JARVIS
              </button>
            </div>
          )}
        </div>

        {shareError && (
          <div className="px-3.5 pb-3 text-[10px] text-rose-300">
            {shareError}
          </div>
        )}
      </div>

      {showOutput && (
        <div className="fixed inset-0 z-[90] bg-slate-950/60 backdrop-blur-xl flex items-center justify-center p-4">
          <div className="w-full max-w-4xl max-h-[82dvh] overflow-hidden rounded-[18px] border border-white/10 bg-slate-900/90 shadow-[0_28px_90px_rgba(2,6,23,0.72)] backdrop-blur-2xl flex flex-col">
            <div className="shrink-0 flex items-center justify-between px-4 py-3 border-b border-white/[0.07] bg-white/[0.025]">
              <div className="flex items-center gap-2 text-sm text-slate-200 font-medium">
                <Bot className="w-4 h-4 text-indigo-400" />
                Execution Output
              </div>

              <button
                type="button"
                onClick={() => setShowOutput(false)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.05]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <pre className="flex-1 min-h-0 overflow-auto p-4 bg-[#020817] text-[11px] sm:text-xs leading-relaxed text-slate-200 whitespace-pre-wrap break-words">
              {output || '(No terminal output captured)'}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}